import styled from '@emotion/styled';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import ArrowIcon from 'assets/icons/arrow-down.svg';
import { colors } from 'constants/colors';
import { SubscriptionLevel } from 'constants/users';
import { selectOptions } from './constants';

// Stessa styled component di components/form/Select/Select.jsx
// (CustomFormControl, non esportata da lì) — replicata qui invece di
// importata per non toccare un file condiviso da ~15 altri select/multiselect
// dell'app. Stesse regole esatte: freccia custom al posto di quella MUI di
// default, font/colore label coerenti con tutte le altre select RHF del
// progetto.
const CustomFormControl = styled(FormControl)(props => ({
  '& svg': {
    backgroundImage: props.disabled ? null : `url(${ArrowIcon})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    fill: 'none',
    backgroundSize: '60%',
  },
  '& label': { fontSize: '0.8rem', fontFamily: 'Ubuntu' },
  '& label.Mui-focused': {
    color: colors.BLACK,
    fontWeight: 'bold',
  },
  // I fratelli TextField-based (RHFTextField/RHFSelect/RHFDatePicker) sono
  // più alti perché il tema (theme.js, override MuiTextField) applica
  // `.MuiOutlinedInput-root { height: '100%' }` — che li fa allungare fino
  // all'altezza della riga Grid (align-items: stretch di default). Questa
  // regola vive scoperta lì SOLO dentro MuiTextField/MuiAutocomplete, non su
  // MuiSelect/MuiFormControl in generale: un Select "nudo" come questo
  // (stessa base di RHFMultiSelect) non la eredita e resta alla sua altezza
  // naturale, più bassa. Stessa regola replicata qui, scoperta a questo solo
  // componente.
  '& .MuiOutlinedInput-root': {
    height: '100%',
  },
}));

// Admin > Gestione Utenti > Dettaglio Utente, campo "Export addizionali - Singolo ponte".
// Componente dedicato (non il condiviso RHFMultiSelect di components/form,
// usato da ~15 altri punti dell'app) perché questo campo ha due esigenze
// che quello non copre e che non vanno introdotte lì: (1) titoletti di
// categoria/sottocategoria sempre visibili nel menu, non selezionabili,
// esattamente come in `selectOptions` sotto — per contestualizzare il tipo
// di esportazione; (2) la lista mostrata dipende dalla tipologia
// dell'utente che si sta editando (`userType`, ENTE/PROFESSIONISTA): solo
// le esportazioni che quella tipologia NON ha già di default.
//
// Filtro, in due parti — un'esportazione compare come "addizionale" se
// l'utente in editing non ce l'ha già di default:
// 1) `visible` non include `userType` (stesso criterio di ExportSelect.jsx)
//    — es. le voci Professionista-only per un Ente, e viceversa;
// 2) `visible` include `userType`, ma la voce è comunque gated da
//    `disabledIfNoSubscription`/`disabledIfNoL3Module` e l'utente in
//    editing — secondo i campi `level`/`expirationDate` dello stesso form —
//    non ha sottoscrizione attiva (o non ha il modulo L3): la vedrebbe
//    disabilitata, quindi va offerta qui come possibile override.
// Un'esportazione senza `visible` (disponibile a tutti) non è mai candidata
// ("ce l'ha già" a prescindere).
//
// Una volta granted da qui, l'override è totale (ExportSelect.jsx bypassa
// sia il controllo di tipologia che quello di sottoscrizione/modulo L3 per
// le voci in user.additionalExports) — la sottoscrizione/modulo qui sopra
// serve solo a decidere QUALI voci proporre come candidate, non a limitare
// cosa l'admin può concedere.
//
// Un gruppo/sottogruppo con zero voci superstiti non produce intestazioni
// orfane.
const buildGroupedOptions = (userType, hasActiveSubscription, hasL3Module) => {
  const groups = [];
  let currentTop = null;
  let currentSub = null;

  selectOptions.forEach(option => {
    if (option.value === 0) {
      currentTop = option.label;
      currentSub = null;
      return;
    }
    if (option.value === null) {
      currentSub = option.label;
      return;
    }
    const lastGroup = groups[groups.length - 1];
    const sameGroup =
      lastGroup && lastGroup.top === currentTop && lastGroup.sub === currentSub;
    const group = sameGroup
      ? lastGroup
      : { top: currentTop, sub: currentSub, items: [] };
    if (!sameGroup) groups.push(group);
    group.items.push(option);
  });

  const rows = [];
  let lastTop;
  let lastSub;
  groups.forEach(group => {
    const items = group.items.filter(option => {
      if (!option.visible) return false;
      if (!option.visible.includes(userType)) return true;
      if (option.disabledIfNoL3Module) return !hasL3Module;
      if (option.disabledIfNoSubscription) return !hasActiveSubscription;
      return false;
    });
    if (!items.length) return;
    if (group.top !== lastTop) {
      rows.push({
        key: `header-top-${rows.length}`,
        value: null,
        label: group.top,
        header: true,
        level: 'top',
      });
      lastTop = group.top;
      lastSub = undefined;
    }
    if (group.sub && group.sub !== lastSub) {
      rows.push({
        key: `header-sub-${rows.length}`,
        value: null,
        label: group.sub,
        header: true,
        level: 'sub',
      });
      lastSub = group.sub;
    }
    items.forEach(option =>
      rows.push({ key: option.value, value: option.value, label: option.label })
    );
  });

  return rows;
};

const AdditionalExportsSelect = ({ userType }) => {
  const { control } = useFormContext();
  const name = 'additionalExports';
  const valueWatch = useWatch({ control, name });
  const theme = useTheme();

  // Stessi campi di InspectorForm.jsx/InstitutionForm.jsx (stesso form) —
  // determinano quali voci gated da sottoscrizione/modulo L3 l'utente in
  // editing NON ha già di default (vedi commento su buildGroupedOptions).
  const expirationDateWatch = useWatch({ control, name: 'expirationDate' });
  const levelWatch = useWatch({ control, name: 'level' });
  const hasActiveSubscription = !!(
    expirationDateWatch && new Date(expirationDateWatch) > new Date()
  );
  const hasL3Module =
    hasActiveSubscription && levelWatch === SubscriptionLevel.StandardL3;

  const rows = useMemo(
    () => buildGroupedOptions(userType, hasActiveSubscription, hasL3Module),
    [userType, hasActiveSubscription, hasL3Module]
  );
  const realRows = useMemo(() => rows.filter(row => !row.header), [rows]);

  // Stesso pattern di RHFMultiSelect (components/form/Select/Select.jsx):
  // uno stato locale `options`, aggiornato SINCRONAMENTE dentro
  // `handleChange` insieme a `field.onChange`, non solo derivato da
  // `useWatch`. Necessario perché il menu resta aperto nello stesso
  // popover durante la selezione multipla: affidarsi solo a `useWatch` per
  // sapere quali voci evidenziare introduce un ritardo di un render (la
  // sottoscrizione RHF) e la voce appena cliccata non risultava mai
  // evidenziata di giallo finché non si richiudeva/riapriva il menu.
  const [options, setOptions] = useState([]);

  useEffect(() => {
    setOptions(
      valueWatch === '' || valueWatch === null || valueWatch === undefined
        ? []
        : valueWatch
    );
  }, [valueWatch]);

  const handleChange = (event, field) => {
    const { value } = event.target;
    const nextValue = typeof value === 'string' ? value.split(',') : value;
    field.onChange(nextValue);
    setOptions(nextValue);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field }) => (
        <CustomFormControl style={{ width: '100%' }}>
          <InputLabel style={{ top: '0px' }} id="additional-exports-label">
            Export addizionali - Singolo ponte
          </InputLabel>
          <Select
            {...field}
            multiple
            label="Export addizionali - Singolo ponte"
            labelId="additional-exports-label"
            value={options}
            onChange={event => handleChange(event, field)}
            renderValue={values => (
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {values.map(value => (
                  <Chip
                    key={value}
                    label={realRows.find(row => row.value === value)?.label}
                    size="small"
                    color="primary"
                  />
                ))}
              </Box>
            )}
          >
            {rows.map(row => {
              // Stesso `pl` (2) su TUTTE le righe — titoli, sottotitoli e
              // voci reali — perché MUI applica un padding di default
              // diverso a una MenuItem `disabled` (i titoli) rispetto a una
              // selezionabile: senza un valore esplicito uguale su
              // entrambe, titoli e voci risultavano disallineati a
              // sinistra.
              if (row.header) {
                return (
                  <MenuItem
                    key={row.key}
                    value={row.value}
                    disabled
                    sx={{
                      opacity: '1 !important',
                      pl: 2,
                      pr: 2,
                      pt: row.level === 'top' ? 1.5 : 0.5,
                      pb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        fontWeight: 'bold',
                        fontSize: row.level === 'top' ? 14 : 12,
                        textTransform: 'uppercase',
                        color: colors.BLACK,
                      }}
                    >
                      {row.label}
                    </Box>
                  </MenuItem>
                );
              }
              // Evidenziazione: stesso identico pattern di RHFMultiSelect
              // (components/form/Select/Select.jsx) — il colore va su un
              // Box INTERNO alla MenuItem, mai sulla MenuItem stessa. Il
              // tema (theme.js, MuiMenuItem) impone
              // `backgroundColor: colors.WHITE !important` su ogni
              // `.MuiMenuItem-root` dell'app: nessun bgcolor/sx messo
              // direttamente sulla MenuItem può vincere quell'!important,
              // solo un elemento figlio (non soggetto a quella regola,
              // scoperta sulla classe MenuItem) può colorarsi davvero.
              const isSelected = options?.some(el => el === row.value);
              return (
                <MenuItem
                  key={row.key}
                  value={row.value}
                  sx={{ padding: 0, pl: 2 }}
                >
                  <Box
                    width={'100%'}
                    p={0.5}
                    bgcolor={isSelected ? theme.palette.primary.light : ''}
                  >
                    {row.label}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </CustomFormControl>
      )}
    />
  );
};

AdditionalExportsSelect.propTypes = {
  userType: PropTypes.string.isRequired,
};

export { AdditionalExportsSelect };
